import { PickerButtonIndicator } from "@/components/picker-with-sheet"
import { FormParamStatus } from "@/lib/form/types"
import { EnumsOf } from "@/lib/utils"
import {
  GetPendingSheetsResponse,
  LastHistorySheet,
  PendingSheet,
  SubmitItem,
  SubmitReading,
  SubmitSheetRequest,
  SubmitV2SheetsRequest,
  SubmitWindowReading,
  V2AllSchemasResponse,
  V2Department,
  V2MarketType,
  V2ParameterSection,
  V2Product,
  V2ProductsMap,
  V2SectionDisplayNames,
  V2SheetStatus,
} from "@/packaging-v2/lib/schema"
import { createEmptyItem } from "@/packaging-v2/lib/form"
import { getProductsByIds } from "@/packaging-v2/lib/helpers"
import { V2FormSubmission } from "@/services/packaging-v2"
import { StateCreator } from "@/store/utils"
import { LabelValue } from "@/types/common"

// ============================================
// Sheet States
// ============================================

export const PackagingV2Sheets = {
  None: "None",
  AddItem: "AddItem",
  AddDept: "AddDept",
  ConfirmEndShift: "ConfirmEndShift",
  DiscardShift: "DiscardShift",
  EndShiftLoading: "EndShiftLoading",
  EndShiftError: "EndShiftError",
  ConfirmDeleteItem: "ConfirmDeleteItem",
  ConfirmSubmit: "ConfirmSubmit",
} as const
export type PackagingV2Sheets = EnumsOf<typeof PackagingV2Sheets>

// ============================================
// Form Data Types
// ============================================

export interface V2WindowFormData {
  id: string
  startTime: string
  endTime: string
  value?: string
  recordedAt?: string
  remark?: string
}

export interface V2ParamFormData {
  id: string
  name: string
  inputType: string
  validationType: string | null
  unit?: string
  minLimit?: number
  maxLimit?: number
  possibleValues?: string[]
  correctValue?: string
  frequencyMinutes?: number | null
  marketType: V2MarketType
  required: boolean
  value?: string
  remark?: string
  recordedAt?: string
  windows?: V2WindowFormData[]
  status: FormParamStatus
}

export interface V2SectionFormData {
  id: string
  type: V2ParameterSection
  name: string
  isExpanded: boolean
  status: FormParamStatus
  parameters: V2ParamFormData[]
}

export interface V2ItemFormData {
  id: string
  sequenceOrder: number
  marketType: V2MarketType
  productId?: string
  productName?: string
  skuId?: string
  skuName?: string

  sections: V2SectionFormData[]
  status: FormParamStatus
}

export interface V2MachineFormData {
  id: string
  name: string
  deptId: string
  items: V2ItemFormData[]
}

export interface V2FormSchema {
  id: string
  name: string
  machines: V2MachineFormSchema[]
}

export interface V2MachineFormSchema {
  id: string
  name: string
  deptId: string
  items: V2ItemFormData[]
}

export interface V2DeptHistory {
  id: string
  sheets: LastHistorySheet[]
}

export interface MachineOption extends LabelValue {}

// ============================================
// State Interface
// ============================================

export interface PackagingV2State {
  shift: {
    processId: string
    shiftId: string
  }
  // Normalized storage
  allDepartments: V2Department[]
  allProducts: V2ProductsMap
  // UI-friendly options
  allDepartmentsOptions: LabelValue[]
  allMachines: Record<string, MachineOption[]>
  formSchemas: V2FormSchema[]
  forms: V2MachineFormData[]
  currDeptId: string
  currMachineId: string
  currItemIndex: number
  currSheet: PackagingV2Sheets
  indicators: Record<string, PickerButtonIndicator>
  submissions: V2FormSubmission[]
  histories: V2DeptHistory[]
}

export function initialState(): PackagingV2State {
  return {
    shift: {
      processId: "",
      shiftId: "",
    },
    allDepartments: [],
    allProducts: {},
    allDepartmentsOptions: [],
    allMachines: {},
    formSchemas: [],
    forms: [],
    currDeptId: "",
    currMachineId: "",
    currItemIndex: 0,
    currSheet: PackagingV2Sheets.None,
    indicators: {},
    submissions: [],
    histories: [],
  }
}

// ============================================
// Slice Interface
// ============================================

export interface PackagingV2Slice {
  packagingV2: PackagingV2State & {
    // Schema management
    setAllSchemas(response: V2AllSchemasResponse): void
    getDeptSchema(deptId: string): V2Department | undefined
    getMachines(deptId: string): MachineOption[]
    getProductsForMachine(machineId: string): V2Product[]
    getProductById(productId: string): V2Product | undefined

    // Navigation
    setCurrSheet(sheet: PackagingV2Sheets): void
    setCurrDeptId(deptId: string): void
    setCurrMachineId(machineId: string): void
    setCurrItemIndex(index: number): void

    // Form schema management
    setDepts(depts: LabelValue[]): void
    addDeptForm(form: V2FormSchema): void
    addDeptForms(forms: V2FormSchema[]): void
    setMachineFormSchema(deptId: string, schema: V2MachineFormSchema): void

    // Form data management
    setMachineFormData(deptId: string, data: V2MachineFormData): void
    getFormData(deptId: string, machineId: string): V2MachineFormData | undefined
    addItem(deptId: string, machineId: string, item: V2ItemFormData): void
    removeItem(deptId: string, machineId: string, itemIndex: number): void
    setItemFormData(
      deptId: string,
      machineId: string,
      itemIndex: number,
      data: Partial<V2ItemFormData>
    ): void
    setSectionFormData(
      deptId: string,
      machineId: string,
      itemIndex: number,
      sectionType: V2ParameterSection,
      data: Partial<V2SectionFormData>
    ): void
    setParamFormData(
      deptId: string,
      machineId: string,
      itemIndex: number,
      sectionType: V2ParameterSection,
      paramId: string,
      data: Partial<V2ParamFormData>
    ): void
    setParamRemark(
      deptId: string,
      machineId: string,
      itemIndex: number,
      sectionType: V2ParameterSection,
      paramId: string,
      remark: string
    ): void
    setWindowRemark(
      deptId: string,
      machineId: string,
      itemIndex: number,
      sectionType: V2ParameterSection,
      paramId: string,
      windowIndex: number,
      remark: string
    ): void

    // Submission management
    addSubmission(submission: V2FormSubmission): void
    getNextSubmission(): V2FormSubmission | undefined
    dequeOldestSubmission(): void
    buildSubmissionPayload(
      deptId: string,
      machineId: string,
      status?: V2SheetStatus
    ): SubmitV2SheetsRequest | null

    // Shift lifecycle
    startShift(details: PackagingV2State["shift"]): void
    clearShift(): void
    clearFormData(): void
    setProcessDetails(details: PackagingV2State["shift"]): void

    // Indicators
    setIndicators(indicators: Record<string, PickerButtonIndicator>): void

    // History
    addHistory(history: V2DeptHistory): void
    getHistory(deptId: string): V2DeptHistory | undefined

    // Cleanup
    removePendingSheets(): void

    // Multi-device sync
    hydrateFromPendingSheets(response: GetPendingSheetsResponse): void

    // Mark item as submitted (called after API success)
    markItemAsSubmitted(deptId: string, machineId: string, itemIndex: number): void
  }
}

// ============================================
// Action name helper
// ============================================

function actionName(action: string) {
  return `packagingV2/${action}`
}

// ============================================
// Slice Creator
// ============================================

const createPackagingV2Slice: StateCreator<PackagingV2Slice> = (set, get) => ({
  packagingV2: {
    ...initialState(),

    // ========== Schema Management ==========

    setAllSchemas(response) {
      set(
        (state) => {
          // Store normalized data directly
          state.packagingV2.allDepartments = response.departments
          state.packagingV2.allProducts = response.products

          // Build UI-friendly options
          const departmentOptions: LabelValue[] = []
          const machines: Record<string, MachineOption[]> = {}

          for (const dept of response.departments) {
            departmentOptions.push({
              id: dept.id,
              label: dept.name,
              value: dept.id,
            })

            for (const machine of dept.machines) {
              if (!machines[dept.id]) {
                machines[dept.id] = []
              }

              machines[dept.id].push({
                id: machine.id,
                label: machine.name,
                value: machine.id,
              })
            }
          }

          state.packagingV2.allDepartmentsOptions = departmentOptions
          state.packagingV2.allMachines = machines

          return state
        },
        undefined,
        actionName("setAllSchemas")
      )
    },

    getDeptSchema(deptId) {
      const state = get().packagingV2
      return state.allDepartments.find((d) => d.id === deptId)
    },

    getProductsForMachine(machineId) {
      const state = get().packagingV2

      // Find which department contains this machine
      for (const dept of state.allDepartments) {
        const machine = dept.machines.find((m) => m.id === machineId)
        if (machine) {
          return getProductsByIds(state.allProducts, machine.products)
        }
      }

      return []
    },

    getProductById(productId) {
      const state = get().packagingV2
      return state.allProducts[productId]
    },

    getMachines(deptId) {
      const state = get().packagingV2
      return state.allMachines[deptId] || []
    },

    // ========== Navigation ==========

    setCurrSheet(sheet) {
      set(
        (state) => {
          state.packagingV2.currSheet = sheet
          return state
        },
        undefined,
        actionName("setCurrSheet")
      )
    },

    setCurrDeptId(deptId) {
      set(
        (state) => {
          state.packagingV2.currDeptId = deptId

          // Set first machine as current
          const form = state.packagingV2.formSchemas.find(
            (item) => item.id === deptId
          )
          if (form && form.machines.length > 0) {
            state.packagingV2.currMachineId = form.machines[0].id
          }

          // Reset item index
          state.packagingV2.currItemIndex = 0

          return state
        },
        undefined,
        actionName("setCurrDeptId")
      )
    },

    setCurrMachineId(machineId) {
      set(
        (state) => {
          state.packagingV2.currMachineId = machineId
          state.packagingV2.currItemIndex = 0
          return state
        },
        undefined,
        actionName("setCurrMachineId")
      )
    },

    setCurrItemIndex(index) {
      set(
        (state) => {
          state.packagingV2.currItemIndex = index
          return state
        },
        undefined,
        actionName("setCurrItemIndex")
      )
    },

    // ========== Form Schema Management ==========

    setDepts(_depts) {
      set(
        (state) => {
          // Update allDepartments with selected depts only
          return state
        },
        undefined,
        actionName("setDepts")
      )
    },

    addDeptForm(form) {
      set(
        (state) => {
          // Prevent duplicate department
          const exists = state.packagingV2.formSchemas.some((f) => f.id === form.id)
          if (!exists) {
            state.packagingV2.formSchemas.push(form)
          }
          return state
        },
        undefined,
        actionName("addDeptForm")
      )
    },

    addDeptForms(forms) {
      set(
        (state) => {
          // Prevent duplicate departments
          const existingIds = new Set(
            state.packagingV2.formSchemas.map((f) => f.id)
          )
          const newForms = forms.filter((f) => !existingIds.has(f.id))
          state.packagingV2.formSchemas.push(...newForms)
          return state
        },
        undefined,
        actionName("addDeptForms")
      )
    },

    setMachineFormSchema(deptId, schema) {
      set(
        (state) => {
          const form = state.packagingV2.formSchemas.find(
            (item) => item.id === deptId
          )

          if (!form) {
            return state
          }

          const machineIdx = form.machines.findIndex((m) => m.id === schema.id)
          if (machineIdx >= 0) {
            form.machines[machineIdx] = schema
          } else {
            form.machines.push(schema)
          }

          return state
        },
        undefined,
        actionName("setMachineFormSchema")
      )
    },

    // ========== Form Data Management ==========

    setMachineFormData(deptId, data) {
      set(
        (state) => {
          const formIdx = state.packagingV2.forms.findIndex(
            (item) => item.deptId === deptId && item.id === data.id
          )

          if (formIdx < 0) {
            state.packagingV2.forms.push(data)
          } else {
            state.packagingV2.forms[formIdx] = data
          }

          return state
        },
        undefined,
        actionName("setMachineFormData")
      )
    },

    getFormData(deptId, machineId) {
      const state = get().packagingV2
      return state.forms.find(
        (item) => item.deptId === deptId && item.id === machineId
      )
    },

    addItem(deptId, machineId, item) {
      set(
        (state) => {
          const form = state.packagingV2.forms.find(
            (f) => f.deptId === deptId && f.id === machineId
          )

          if (form) {
            form.items.push(item)
          }

          return state
        },
        undefined,
        actionName("addItem")
      )
    },

    removeItem(deptId, machineId, itemIndex) {
      set(
        (state) => {
          const form = state.packagingV2.forms.find(
            (f) => f.deptId === deptId && f.id === machineId
          )

          if (!form || !form.items[itemIndex]) {
            return state
          }

          form.items.splice(itemIndex, 1)

          // Reconcile queued submissions for this sheet after item removal.
          state.packagingV2.submissions = state.packagingV2.submissions
            .filter((submission) => {
              const isTargetSheet =
                submission.deptId === deptId &&
                submission.machineId === machineId

              if (!isTargetSheet) {
                return true
              }

              // Drop the deleted item from queue.
              return submission.itemIndex !== itemIndex
            })
            .map((submission) => {
              const isTargetSheet =
                submission.deptId === deptId &&
                submission.machineId === machineId

              if (!isTargetSheet || submission.itemIndex < itemIndex) {
                return submission
              }

              // Shift queued indexes after removed position.
              return {
                ...submission,
                itemIndex: submission.itemIndex - 1,
              }
            })

          const isCurrentSheet =
            state.packagingV2.currDeptId === deptId &&
            state.packagingV2.currMachineId === machineId

          // Keep machine sheet visible: reset to initial single empty item when last item is deleted.
          if (form.items.length === 0) {
            form.items.push(createEmptyItem(1))
            delete state.packagingV2.indicators[`${deptId}.${machineId}`]
          }

          if (isCurrentSheet) {
            if (state.packagingV2.currItemIndex > itemIndex) {
              state.packagingV2.currItemIndex -= 1
            } else if (state.packagingV2.currItemIndex === itemIndex) {
              state.packagingV2.currItemIndex = Math.min(
                itemIndex,
                form.items.length - 1
              )
            }
          }

          return state
        },
        undefined,
        actionName("removeItem")
      )
    },

    setItemFormData(deptId, machineId, itemIndex, data) {
      set(
        (state) => {
          const form = state.packagingV2.forms.find(
            (f) => f.deptId === deptId && f.id === machineId
          )

          if (form && form.items[itemIndex]) {
            Object.assign(form.items[itemIndex], data)
          }

          return state
        },
        undefined,
        actionName("setItemFormData")
      )
    },

    setSectionFormData(deptId, machineId, itemIndex, sectionType, data) {
      set(
        (state) => {
          const form = state.packagingV2.forms.find(
            (f) => f.deptId === deptId && f.id === machineId
          )

          if (form && form.items[itemIndex]) {
            const section = form.items[itemIndex].sections.find(
              (s) => s.type === sectionType
            )
            if (section) {
              Object.assign(section, data)
            }
          }

          return state
        },
        undefined,
        actionName("setSectionFormData")
      )
    },

    setParamFormData(deptId, machineId, itemIndex, sectionType, paramId, data) {
      set(
        (state) => {
          const form = state.packagingV2.forms.find(
            (f) => f.deptId === deptId && f.id === machineId
          )

          if (form && form.items[itemIndex]) {
            const section = form.items[itemIndex].sections.find(
              (s) => s.type === sectionType
            )
            if (section) {
              const param = section.parameters.find((p) => p.id === paramId)
              if (param) {
                Object.assign(param, data)
              }
            }
          }

          return state
        },
        undefined,
        actionName("setParamFormData")
      )
    },

    setParamRemark(deptId, machineId, itemIndex, sectionType, paramId, remark) {
      set(
        (state) => {
          const form = state.packagingV2.forms.find(
            (f) => f.deptId === deptId && f.id === machineId
          )

          if (form && form.items[itemIndex]) {
            const section = form.items[itemIndex].sections.find(
              (s) => s.type === sectionType
            )
            if (section) {
              const param = section.parameters.find((p) => p.id === paramId)
              if (param) {
                param.remark = remark
              }
            }
          }

          return state
        },
        undefined,
        actionName("setParamRemark")
      )
    },

    setWindowRemark(deptId, machineId, itemIndex, sectionType, paramId, windowIndex, remark) {
      set(
        (state) => {
          const form = state.packagingV2.forms.find(
            (f) => f.deptId === deptId && f.id === machineId
          )

          if (form && form.items[itemIndex]) {
            const section = form.items[itemIndex].sections.find(
              (s) => s.type === sectionType
            )
            if (section) {
              const param = section.parameters.find((p) => p.id === paramId)
              if (param && param.windows && param.windows[windowIndex]) {
                param.windows[windowIndex].remark = remark
              }
            }
          }

          return state
        },
        undefined,
        actionName("setWindowRemark")
      )
    },

    // ========== Submission Management ==========

    addSubmission(submission) {
      set(
        (state) => {
          // Prevent duplicate submissions for the same dept/machine/item
          const isDuplicate = state.packagingV2.submissions.some(
            (s) =>
              s.deptId === submission.deptId &&
              s.machineId === submission.machineId &&
              s.itemIndex === submission.itemIndex
          )

          if (!isDuplicate) {
            state.packagingV2.submissions.push(submission)
          }

          return state
        },
        undefined,
        actionName("addSubmission")
      )
    },

    getNextSubmission() {
      const state = get().packagingV2
      return state.submissions[0]
    },

    dequeOldestSubmission() {
      set(
        (state) => {
          state.packagingV2.submissions.shift()
          return state
        },
        undefined,
        actionName("dequeOldestSubmission")
      )
    },

    buildSubmissionPayload(deptId, machineId, status = V2SheetStatus.SUBMITTED): SubmitV2SheetsRequest | null {
      const state = get().packagingV2
      const form = state.forms.find(
        (f) => f.deptId === deptId && f.id === machineId
      )

      if (!form || form.items.length === 0) {
        return null
      }

      const items: SubmitItem[] = []

      for (const item of form.items) {
        if (!item.productId || !item.skuId) {
          continue
        }

        const readings: SubmitReading[] = []

        for (const section of item.sections) {
          for (const param of section.parameters) {
            // Include params that have values (either direct value or window values)
            const hasValue = param.value || (param.windows && param.windows.some((w) => w.value))
            if (!hasValue) {
              continue
            }

            const reading: SubmitReading = {
              parameterId: param.id,
              value: param.value || "",
              remark: param.remark,
              recordedAt: param.recordedAt || new Date().toISOString(),
            }

            // Handle window readings for frequency-based params
            if (param.windows && param.windows.length > 0) {
              reading.windowReadings = param.windows
                .filter((w) => w.value)
                .map(
                  (w): SubmitWindowReading => ({
                    value: w.value || "",
                    startTime: w.startTime,
                    endTime: w.endTime,
                    recordedAt: w.recordedAt || new Date().toISOString(),
                    remark: w.remark,
                  })
                )
            }

            readings.push(reading)
          }
        }

        if (readings.length > 0) {
          items.push({
            productId: item.productId,
            skuId: item.skuId,
            marketType: item.marketType,

            sequenceOrder: item.sequenceOrder,
            readings,
          })
        }
      }

      if (items.length === 0) {
        return null
      }

      const sheet: SubmitSheetRequest = {
        departmentId: deptId,
        machineId: machineId,
        items,
        status,
      }

      return [sheet]
    },

    // ========== Shift Lifecycle ==========

    startShift(details) {
      set(
        (state) => {
          state.packagingV2.shift = details
          return state
        },
        undefined,
        actionName("startShift")
      )
    },

    clearShift() {
      set(
        (state) => {
          state.session.session.processes =
            state.session.session.processes.filter(
              (proc) => proc.processId !== state.packagingV2.shift.processId
            )

          state.packagingV2 = {
            ...state.packagingV2,
            ...initialState(),
            allDepartments: state.packagingV2.allDepartments,
            allProducts: state.packagingV2.allProducts,
            allDepartmentsOptions: state.packagingV2.allDepartmentsOptions,
            allMachines: state.packagingV2.allMachines,
          }

          return state
        },
        undefined,
        actionName("clearShift")
      )
    },

    clearFormData() {
      set(
        (state) => {
          state.packagingV2.formSchemas = []
          state.packagingV2.forms = []
          state.packagingV2.currDeptId = ""
          state.packagingV2.currMachineId = ""
          state.packagingV2.currItemIndex = 0
          state.packagingV2.currSheet = PackagingV2Sheets.None
          state.packagingV2.submissions = []
          return state
        },
        undefined,
        actionName("clearFormData")
      )
    },

    setProcessDetails(details) {
      set(
        (state) => {
          state.packagingV2.shift = details
          return state
        },
        undefined,
        actionName("setProcessDetails")
      )
    },

    // ========== Indicators ==========

    setIndicators(indicators) {
      set(
        (state) => {
          state.packagingV2.indicators = indicators
          return state
        },
        undefined,
        actionName("setIndicators")
      )
    },

    // ========== History ==========

    addHistory(history) {
      set(
        (state) => {
          const existingIdx = state.packagingV2.histories.findIndex(
            (h) => h.id === history.id
          )
          if (existingIdx >= 0) {
            state.packagingV2.histories[existingIdx] = history
          } else {
            state.packagingV2.histories.push(history)
          }
          return state
        },
        undefined,
        actionName("addHistory")
      )
    },

    getHistory(deptId) {
      const state = get().packagingV2
      return state.histories.find((history) => history.id === deptId)
    },

    // ========== Cleanup ==========

    removePendingSheets() {
      set(
        (state) => {
          const filled = new Set(
            Object.entries(state.packagingV2.indicators)
              .filter(([_, indicator]) => {
                return indicator === PickerButtonIndicator.Success
              })
              .map((item) => item[0])
          )

          state.packagingV2.formSchemas = state.packagingV2.formSchemas.map(
            (schema) => {
              schema.machines = schema.machines.filter((machine) => {
                const key = `${schema.id}.${machine.id}`
                return filled.has(key)
              })
              return schema
            }
          )

          state.packagingV2.formSchemas = state.packagingV2.formSchemas.filter(
            (schema) => schema.machines.length > 0
          )

          return state
        },
        undefined,
        actionName("removePendingSheets")
      )
    },

    // ========== Multi-device Sync ==========

    hydrateFromPendingSheets(response: GetPendingSheetsResponse) {
      set(
        (state) => {
          if (!response.sheets.length) {
            return state
          }

          // Group sheets by department
          const deptMap = new Map<
            string,
            { name: string; sheets: PendingSheet[] }
          >()
          for (const sheet of response.sheets) {
            if (!deptMap.has(sheet.departmentId)) {
              deptMap.set(sheet.departmentId, {
                name: sheet.departmentName,
                sheets: [],
              })
            }
            deptMap.get(sheet.departmentId)!.sheets.push(sheet)
          }

          // Build formSchemas and forms from pending sheets
          const formSchemas: V2FormSchema[] = []
          const forms: V2MachineFormData[] = []

          for (const [deptId, deptData] of deptMap) {
            // Create form schema for this department
            const formSchema: V2FormSchema = {
              id: deptId,
              name: deptData.name,
              machines: deptData.sheets.map((sheet) => ({
                id: sheet.machineId,
                name: sheet.machineName,
                deptId: deptId,
                items: [], // Items are stored in forms, not formSchemas
              })),
            }
            formSchemas.push(formSchema)

            // Create machine form data for each sheet
            for (const sheet of deptData.sheets) {
              // Map V2SheetStatus to FormParamStatus
              const formStatus = sheet.status === V2SheetStatus.DRAFT 
                ? FormParamStatus.DRAFT 
                : FormParamStatus.SUBMITTED

              const machineFormData: V2MachineFormData = {
                id: sheet.machineId,
                name: sheet.machineName,
                deptId: deptId,
                items: sheet.items.map((item) => {
                  // Convert sections from API format to store format
                  const sections: V2SectionFormData[] = item.sections.map(
                    (apiSection) => ({
                      id: `section-${apiSection.section}-${Date.now()}-${Math.random()}`,
                      type: apiSection.section,
                      name: V2SectionDisplayNames[apiSection.section],
                      isExpanded: true,
                      status: formStatus,
                      parameters: apiSection.readings.map((reading) => ({
                        id: reading.parameterId,
                        name: reading.parameterName,
                        inputType: "VALUE_TEXT", // Will be overridden when schema loads
                        validationType: null,
                        unit: reading.unit,
                        minLimit: reading.minLimit,
                        maxLimit: reading.maxLimit,
                        marketType: item.marketType,
                        required: true,
                        value: reading.value,
                        remark: reading.remark,
                        recordedAt: reading.recordedAt,
                        windows: reading.windowReadings?.map((wr) => ({
                          id: wr.id,
                          startTime: wr.startTime,
                          endTime: wr.endTime,
                          value: wr.value,
                          recordedAt: wr.recordedAt,
                          remark: wr.remark,
                        })),
                        status: formStatus,
                      })),
                    })
                  )

                  const itemFormData: V2ItemFormData = {
                    id: `item-${item.sequenceOrder}-${Date.now()}-${Math.random()}`,
                    sequenceOrder: item.sequenceOrder,
                    marketType: item.marketType,
                    productId: item.productId,
                    productName: item.productName,
                    skuId: item.skuId,
                    skuName: item.skuName,

                    sections,
                    status: formStatus,
                  }

                  return itemFormData
                }),
              }
              forms.push(machineFormData)
            }
          }

          // Update store state
          state.packagingV2.formSchemas = formSchemas
          state.packagingV2.forms = forms

          // Set current navigation state to first department/machine
          if (formSchemas.length > 0) {
            state.packagingV2.currDeptId = formSchemas[0].id
            if (formSchemas[0].machines.length > 0) {
              state.packagingV2.currMachineId = formSchemas[0].machines[0].id
            }
          }
          state.packagingV2.currItemIndex = 0

          return state
        },
        undefined,
        actionName("hydrateFromPendingSheets")
      )
    },

    markItemAsSubmitted(deptId, machineId, itemIndex) {
      set(
        (state) => {
          const form = state.packagingV2.forms.find(
            (f) => f.deptId === deptId && f.id === machineId
          )

          if (!form || !form.items[itemIndex]) {
            return state
          }

          const item = form.items[itemIndex]

          // Mark all sections as submitted (except INFORMATION_FIELDS which is handled separately)
          for (const section of item.sections) {
            if (section.status === FormParamStatus.SUBMITTED) continue

            section.status = FormParamStatus.SUBMITTED

            // Mark all params with values as submitted
            for (const param of section.parameters) {
              if (param.value || (param.windows && param.windows.some((w) => w.value))) {
                param.status = FormParamStatus.SUBMITTED
              }
            }
          }

          // Mark the entire item as submitted
          item.status = FormParamStatus.SUBMITTED

          return state
        },
        undefined,
        actionName("markItemAsSubmitted")
      )
    },
  },
})

export default createPackagingV2Slice
